import { BorderStyle, getConfigByRect, StrokeLineCap, StrokeLineJoin } from '@editor/src/js/constants';
import { transBorderDashArray } from '@editor/src/js/utils';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import type { DocumentElement, ParagraphElement, TextElement } from '@tencent/alloy-editor';
import { LineSpacingType } from '@tencent/alloy-editor';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { STMarkerStyleDmlC } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type {
    GeometryShape,
    LineStyle,
    ShapeOriginStyle,
    ShapeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import {
    CustomStyles,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { ILineCap } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import type {
    IBackgroundFillInfo,
    IGradFill,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import type { Marker } from '@tencent/chart/lib/antv/g2/interface';
import type {
    ShapeProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { LineCap, LineJoin } from '@tencent/alloy-renderer';
import {
    getEffectFilter,
    getFillStyle,
    getStrokeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';

/**
 * 获取元素样式字段配置项
 */
export interface OrgStyleOpts {
    filter?: (keyof ShapeOriginStyle)[], // 过滤特定字段
    isLine?: boolean,  // 线段相关。是线段还是边框，返回的默认属性不同
}

export interface ShapeAttrOpts extends OrgStyleOpts {
    // 获取图形样式配置项
}

export const DEFAULT_FONT_SIZE = 18;
export const getTextAttr = (text, defaultProps?: any) => {
    const style = {
        ...defaultProps?.paragraphList?.[0].paragraphProps?.defaultRunProps,
        ...text?.paragraphList?.[0].paragraphProps?.defaultRunProps,
        ...text?.paragraphList?.[0]?.textRuns[0]?.styles,
    };

    // TODO 确认有没有默认fontSize
    let fontSize = _get(style, 'fontSize') || DEFAULT_FONT_SIZE;
    if (fontSize) {
        fontSize = fontSize * CANVAS_PT_SCALE;
    }

    return {
        text: text?.paragraphList?.[0]?.textRuns.map(run => run.text).join(''),
        style: {
            fill: _get(style, 'color'),
            fontSize,
            fontStyle: _get(style, 'fontStyle'),
            fontWeight: _get(style, 'fontWeight'),
            lineHeight: fontSize * 1.2,
        },
    };
};

export const getTextAttrByDocElement = (docElement: DocumentElement) => {
    const firstParagraph: ParagraphElement = docElement.children[0];
    const firstTextRun: TextElement = firstParagraph?.children[0];
    if (firstParagraph && firstTextRun) {
        // 计算行高
        const fontSize = firstTextRun.fontSize || 12;
        let lineHeight = fontSize * 1.2;
        if (firstParagraph.lineSpacing) {
            if (firstParagraph.lineSpacingRule === LineSpacingType.Multiple) {
                lineHeight = fontSize * firstParagraph.lineSpacing;
            } else if (firstParagraph.lineSpacingRule === LineSpacingType.Exactly) {
                lineHeight = firstParagraph.lineSpacing;
            }
        }
        const { text, color, fillBackground, fontWeight, fontFamily } = firstTextRun;
        return {
            text,
            style: {
                fontSize,
                fill: color || translateFill(fillBackground),
                fontStyle: firstTextRun.italic ? 'italic' : '',
                fontWeight,
                fontFamily,
                lineHeight,
            },
        };
    }
    return { };
};

// 获取first textRun的fontSize
export const getFontSize = (docElement: DocumentElement): number => docElement.children[0]?.children[0]?.fontSize || 0;


export const getShapeAttr = (props: ShapeProps['props'], opts?: ShapeAttrOpts): GeometryShape => {
    const { border: borderProps, fillBackground } = props?.style || {};

    const border = getBorderStyle(borderProps!, opts?.isLine);
    return {
        fill: translateFill(fillBackground!),
        border,
        ...getOriginStyle(props, opts),
    };
};

export const filterShapeAttr = (shapeAttr): GeometryShape => {
    if (shapeAttr?.fill === NO_SUPPORT_FILL_SYMBOL) {
        delete shapeAttr.fill;

        return shapeAttr;
    }

    return shapeAttr;
};

export const getBorderStyle = (border: IBorderInfo, isLine?): ShapeStyle => {
    if (_isEmpty(border)) return {};
    // ppt里设置宽度为0，实际表现效果和0.25一样
    const width = border.width === 0 ? 0.25 : border.width;
    const borderStyle: ShapeStyle = {
        lineWidth: width || 1.5,
        // @ts-ignore
        originLineWidth: border.width,
        stroke: translateBorderColor(border),
    };


    /**
     * 端点类型
     * 存在linedash没有端点类型的场景，导致边框绘制不出来，所以默认为butt
     */
    const defaultLineCap = isLine ? 'round' : 'butt';  // 如果是线段（不是边框），office默认lineCap是圆
    borderStyle.lineCap = StrokeLineCap.find(item => item.type ===  border.linecap)?.val as LineStyle['lineCap'] ?? defaultLineCap;

    // 连接点类型
    borderStyle.lineJoin = StrokeLineJoin.find(item => item.type === border.linejoin)?.val as LineStyle['lineJoin'] ?? 'round';

    // 线条类型
    const lineDash = getLineDash({
        ...borderStyle,
        style: border.style,
    });
    if (lineDash) {
        borderStyle.lineDash = lineDash;
    }

    return borderStyle;
};

export const getLineDash = (border): Array<number> | undefined => {
    // 线条类型
    const presetDash = BorderStyle.find(item => item.style === border.style);
    if (presetDash) {
        let presetVal;
        if (border.lineCap === LineCap.Butt) {
            presetVal = presetDash.val2;
        } else {
            presetVal = presetDash.val;
        }

        if (presetVal) {
            return transBorderDashArray(presetVal, border.lineWidth).split(' ')
                .map(item => Number(item));
        }
    }

    return;
};

/**
 * 赋值线段默认属性。
 * 规则：
 * 1. 如果是线段lineCap默认为round，如果是边框默认为butt
 * 2. lineJoin默认都是round
 * @param outline
 * @param isLine 是线段还是边框
 */
export function applyDefaultLineProperty(outline?: IBorderInfo, isLine?): IBorderInfo | undefined {
    if (!_isEmpty(outline)) {
        const defaultLineCap = isLine ? ILineCap.Round : ILineCap.Butt;  // 如果是线段（不是边框），office默认lineCap是圆
        return { ...outline,
            linejoin: outline?.linejoin ?? LineJoin.Round,
            linecap: outline?.linecap ?? defaultLineCap,
        };
    }
}

export function getLineLegendMarkerSymbol(x, y, r, width, height): any[] {
    return [
        ['M', x - (width / 2), y],
        ['L', x + (width / 2), y],
    ];
}

export function getMappingSymbol(symbol?: STMarkerStyleDmlC): Marker {
    if (!symbol) return 'circle';
    return {
        [STMarkerStyleDmlC.square]: 'square',
        [STMarkerStyleDmlC.diamond]: 'diamond',
        [STMarkerStyleDmlC.triangle]: 'triangle',
        [STMarkerStyleDmlC.x]: 'cross',
        [STMarkerStyleDmlC.star]: 'cross', // TODO
        [STMarkerStyleDmlC.dot]: 'hyphen',
        [STMarkerStyleDmlC.dash]: 'hyphen', // TODO
        [STMarkerStyleDmlC.circle]: 'circle',
        [STMarkerStyleDmlC.plus]: 'plus',
        [STMarkerStyleDmlC.picture]: 'circle', // TODO
    }[symbol];
}

function translateGradFill(gradFill: IGradFill): string {
    const { gsList, lin, pathGradient } = gradFill;
    const gs = gsList.map(item => `${item.pos * 0.01}:${item.color?.srgb_color}`);
    if (!lin) {
        const { fillToRect } = pathGradient;
        const { val } = getConfigByRect(fillToRect);
        const { cx, cy, r } = val;
        return `r(${[cx, cy, r]}) ${gs.join(' ')}`;
    }
    const { ang } = lin;
    return `l(${ang}) ${gs.join(' ')}`;
}

export const NO_SUPPORT_FILL_SYMBOL = 'noSupport';

export function translateFill(fillBackground: IBackgroundFillInfo): string {
    if (!fillBackground) return '';
    const { solidFill, gradFill, noFill, pattFill, image } = fillBackground;
    if (noFill) {
        return 'transparent';
    }
    if (solidFill) {
        return solidFill.color.srgb_color;
    }
    if (gradFill) {
        return translateGradFill(gradFill);
    }
    if (pattFill || image) {
        return 'transparent';
    }

    return '';
}

// fillBackground可能包含color，表示预览色， 所以将border与fill分开处理
export function translateBorderColor(border): string {
    if (!border) return '';
    const { color, gradFill, noFill, pattFill, image } = border;
    if (color) {
        return border.color;
    }
    if (noFill) {
        return 'transparent';
    }
    if (gradFill) {
        return translateGradFill(gradFill);
    }
    if (pattFill || image) {
        return NO_SUPPORT_FILL_SYMBOL;
    }
    return '';
}

/**
 * 获取原始的图形样式数据。自定义渲染会用到
 * @param props
 * @param opts 配置项
 */
export function getOriginStyle(props: ShapeProps['props'], opts?: OrgStyleOpts): ShapeOriginStyle {
    if (!props?.style) return { };
    const { filter, isLine = false } = opts || { };
    const { border, fillBackground, effect } = props?.style || {};
    const org = { background: fillBackground, effects: effect, outline: applyDefaultLineProperty(border, isLine) };
    if (Array.isArray(filter)) {
        filter.forEach((key) => {
            delete org[key];
        });
    }
    return org;
}

/**
 * 从总多数据中过滤出原始样式数据
 * @param originStyle
 */
export function filterOriginStyle(originStyle: ShapeOriginStyle): ShapeOriginStyle {
    if (!originStyle) return { };
    const { background, effects, outline } = originStyle || {};
    return { background, effects, outline };
}

/**
 * 根据原始数据生成自定义渲染数据
 * @param originStyle
 */
export function generateCustomStyles(originStyle: ShapeOriginStyle): CustomStyles {
    if (!originStyle) return { };
    const { background, outline, effects } = originStyle;
    // 必须rotateWithShape设置为false，否则渐变效果不对
    const fillStyle = getFillStyle({ fillBackground: background }, undefined, true);
    const strokeStyle = getStrokeStyle({ style: { border: outline } });
    const effectStyle = getEffectFilter(effects);
    return { fillStyle, strokeStyle, effects: effectStyle };
}

export const LINE_BG_KEYS = ['color', 'gradFill', 'noFill'];

/**
 * 赋值边框的背景
 * @param line
 * @param bg
 */
export function applyBgToLineStroke(line?: IBorderInfo, bg?: IBackgroundFillInfo): IBorderInfo {
    if (!line || !bg) return { };
    LINE_BG_KEYS.forEach((key) => {
        line[key] = bg[key];
    });

    // line的纯色填充的key为color
    if (bg.solidFill) {
        line.color = bg.solidFill.color.srgb_color;
    }

    return line;
}

/**
 * 创建使用边框背景，创建新的背景填充对象
 * @param line
 */
export function createBgByLineStroke(line?: IBorderInfo): IBackgroundFillInfo {
    if (!line) return { };
    const bg = { };
    LINE_BG_KEYS.forEach((key) => {
        if (line[key]) {
            // 有值才赋值，防止上层解构覆盖原值
            bg[key] = line[key];
        }
    });

    if (line.color) {
        (bg as any).solidFill = { color: { srgb_color: line.color } };
    }

    return bg;
}
